<template>
    <button
        v-if="!hideMenu"
        class="menu _button"
        @click.stop="openUserMenu"
        v-tooltip="$t('menu')"
    >
        <mg-icon icon="dots-three-outline" />
    </button>
    <button
        v-if="isLoggedIn && !isCurrentUser(user)"
        class="kpoogebi _button follow-button"
        :class="{
            wait,
            active: isFollowing || hasPendingFollowRequestFromYou,
            full,
            large,
            blocking: isBlocking,
        }"
        :disabled="wait"
        @click.stop="onClick"
        :aria-label="`${state} ${magTransUsername(user)}`"
        v-tooltip="full ? null : `${state} ${magTransUsername(user)}`"
    >
        <template v-if="!wait">
            <template v-if="isBlocking">
                <span>{{ state = $t("blocked") }}</span>
                <mg-icon icon="prohibit" />
            </template>
            <template
                v-else-if="
                    hasPendingFollowRequestFromYou &&
                    magTransProperty(user, 'is_locked', 'isLocked')
                "
            >
                <span>{{ state = $t("followRequestPending") }}</span>
                <mg-icon icon="hourglass-medium" />
            </template>
            <template
                v-else-if="
                    hasPendingFollowRequestFromYou &&
                    !magTransProperty(user, 'is_locked', 'isLocked')
                "
            >
                <!-- つまりリモートフォローの場合。 -->
                <span>{{ state = $t("processing") }}</span>
                <MagSpinner />
            </template>
            <template v-else-if="isFollowing">
                <span>{{ state = $t("unfollow") }}</span>
                <mg-icon icon="minus" />
            </template>
            <template
                v-else-if="
                    !isFollowing &&
                    magTransProperty(user, 'is_locked', 'isLocked')
                "
            >
                <span>{{ state = $t("followRequest") }}</span>
                <mg-icon icon="plus" />
            </template>
            <template
                v-else-if="
                    !isFollowing &&
                    !magTransProperty(user, 'is_locked', 'isLocked')
                "
            >
                <span>{{ state = $t("follow") }}</span>
                <mg-icon icon="plus" />
            </template>
        </template>
        <template v-else>
            <span>{{ state = $t("processing") }}</span>
            <MagSpinner />
        </template>
    </button>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import type * as Misskey from "calckey-js";
import {
    useEventEmitter,
    useLegacyStreamChannel,
} from "@/composables/use-stream";
import { packed } from "magnetar-common";
import { magTransProperty, magTransUsername } from "@/scripts-mag/mag-util";
import { useFluent } from "fluent-vue";
import { useAccountStore } from "@/model/account";
import { storeToRefs } from "pinia";
import { messageConfirmLegacy } from "@/composables/message-box";
import { legacyApi } from "@/api-client";
import {
    ContextMenuUser,
    magContextMenu,
} from "@/components/context-menu/mag-context-menu";

const { $t } = useFluent();
const accountStore = useAccountStore();
const { isCurrentUser, isLoggedIn } = storeToRefs(accountStore);

const emit = defineEmits(["refresh"]);
const props = withDefaults(
    defineProps<{
        user: packed.PackUserMaybeAll | Misskey.entities.User;
        full?: boolean;
        large?: boolean;
        hideMenu?: boolean;
    }>(),
    {
        full: false,
        large: false,
    },
);

const isBlocking = computed(() =>
    magTransProperty(props.user, "you_block", "isBlocking"),
);

const state = ref($t("processing"));

const isFollowing = ref(
    magTransProperty(props.user, "you_follow", "isFollowing"),
);
const hasPendingFollowRequestFromYou = ref(
    magTransProperty(
        props.user,
        "you_request_follow",
        "hasPendingFollowRequestFromYou",
    ),
);
const wait = ref(false);

if (
    typeof magTransProperty(props.user, "you_follow", "isFollowing") !==
    "boolean"
) {
    legacyApi("users/show", {
        userId: props.user.id,
    }).then(onFollowChange);
}

function onFollowChange(user: Misskey.entities.User) {
    if (!("isFollowing" in user && "hasPendingFollowRequestFromYou" in user))
        return;

    if (user.id === props.user.id) {
        isFollowing.value = user.isFollowing;
        hasPendingFollowRequestFromYou.value =
            user.hasPendingFollowRequestFromYou;
    }
}

async function onClick() {
    wait.value = true;

    try {
        if (isBlocking.value) {
            const { canceled } = await messageConfirmLegacy({
                type: "warning",
                text: $t("unblockConfirm"),
                destructive: true,
            });
            if (canceled) return;

            await legacyApi("blocking/delete", {
                userId: props.user.id,
            });
            if (magTransProperty(props.user, "mute", "isMuted")) {
                await legacyApi("mute/delete", {
                    userId: props.user.id,
                });
            }
            emit("refresh");
        } else if (isFollowing.value) {
            const { canceled } = await messageConfirmLegacy({
                type: "warning",
                text: $t("unfollowConfirm", {
                    name: magTransUsername(props.user),
                }),
                destructive: true,
            });

            if (canceled) return;

            await legacyApi("following/delete", {
                userId: props.user.id,
            });
        } else {
            if (hasPendingFollowRequestFromYou.value) {
                await legacyApi("following/requests/cancel", {
                    userId: props.user.id,
                });
                hasPendingFollowRequestFromYou.value = false;
            } else {
                await legacyApi("following/create", {
                    userId: props.user.id,
                });
                hasPendingFollowRequestFromYou.value = true;
            }
        }
    } catch (err) {
        console.error(err);
    } finally {
        wait.value = false;
    }
}

function openUserMenu(e: MouseEvent) {
    magContextMenu({ user: props.user }, ContextMenuUser, {
        parent: e.target as HTMLButtonElement,
    });
}

const legacyMainChannel = useLegacyStreamChannel("main");

useEventEmitter(legacyMainChannel, "follow", onFollowChange);
useEventEmitter(legacyMainChannel, "unfollow", onFollowChange);
</script>

<style lang="scss" scoped>
.menu {
    width: 3em;
    height: 2em;
    vertical-align: middle;
    contain: content;
}

.follow-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: var(--accent);
    border: solid 1px var(--accent);
    padding: 0;
    font-size: 16px;
    width: 2em;
    height: 2em;
    border-radius: 100px;
    background: var(--bg);
    vertical-align: middle;
    contain: content;

    &.full {
        padding: 0.2em 0.7em;
        width: auto;
        font-size: 14px;
    }

    &.large {
        font-size: 16px;
        height: 38px;
        padding: 0 12px 0 16px;
    }

    &:not(.full) {
        width: 31px;

        span {
            display: none;
        }
    }

    &:focus-visible {
        &:after {
            content: "";
            pointer-events: none;
            position: absolute;
            top: -5px;
            right: -5px;
            bottom: -5px;
            left: -5px;
            border: 2px solid var(--focus);
            border-radius: 32px;
        }
    }

    &:hover {
        background-color: color-mix(in srgb, var(--accent) 20%, transparent);
    }

    &:active {
        background-color: color-mix(in srgb, var(--accent) 40%, transparent);
    }

    &.active {
        color: var(--fgOnAccent);
        background: var(--accent);

        &:hover {
            background: var(--accentLighten);
            border-color: var(--accentLighten);
        }

        &:active {
            background: var(--accentDarken);
            border-color: var(--accentDarken);
        }
    }

    &.wait {
        cursor: wait !important;
    }

    > span {
        margin-right: 6px;
    }
}

.blocking {
    background-color: var(--bg) !important;
    border: none;
}
</style>
